// src/pages/auth/Login.js - login, group registration and "forgotten password".
//
// Audit FL-23: the error from login() is shown as returned (wrong password / network /
// server / deactivated account), all three forms have a busy state, fields have labels,
// names and autocomplete for password managers, the password needs at least 8
// characters, the new session is started through AuthContext (startSession), and the
// password-reset answer mentions the spam folder and a contact. An expired session is
// explained (FL-03). Dialogs use the shared Modal (FL-29, FL-37).
import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { CheckCircle } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useMeta } from '../../contexts/MetaContext';
import api, { getErrorMessage, MESSAGES } from '../../services/api';
import { Alert, Button, Modal } from '../../components/ui';
import TextField from '../../components/leader/TextField';
import PasswordField from '../../components/leader/PasswordField';

const MIN_PASSWORD = 8;
const CONTACT_EMAIL = 'info@bestofthebest.cz';
const EMPTY_REGISTRATION = {
  email: '',
  password: '',
  confirmPassword: '',
  groupName: '',
  leaderName: '',
  phone: '',
  consent: false,
};

function RegistrationDialog({ onClose }) {
  const { startSession } = useAuth();
  const [data, setData] = useState(EMPTY_REGISTRATION);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const set = (key) => (e) => setData({ ...data, [key]: e.target.type === 'checkbox' ? e.target.checked : e.target.value });

  const passwordTooShort = data.password.length > 0 && data.password.length < MIN_PASSWORD;
  const mismatch = data.confirmPassword.length > 0 && data.password !== data.confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    const payload = {
      email: data.email.trim().toLowerCase(),
      password: data.password,
      groupName: data.groupName.trim(),
      leaderName: data.leaderName.trim(),
      phone: data.phone.trim(),
    };
    if (!payload.email || !payload.groupName || !payload.leaderName || !payload.phone) {
      setError('Vyplňte prosím všechna pole.');
      return;
    }
    if (payload.password.length < MIN_PASSWORD) {
      setError(`Heslo musí mít alespoň ${MIN_PASSWORD} znaků.`);
      return;
    }
    if (data.password !== data.confirmPassword) {
      setError('Hesla se neshodují.');
      return;
    }
    if (!data.consent) {
      setError('Pro registraci je potřeba souhlas se zpracováním osobních údajů.');
      return;
    }
    setIsSubmitting(true);
    try {
      const response = await api.post('/auth/register', payload);
      // AuthContext stores the session; the /login route then redirects to the dashboard.
      startSession(response.data.token, response.data.user);
    } catch (err) {
      setError(getErrorMessage(err, 'Registrace se nepodařila. Zkuste to prosím znovu.'));
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="Registrace taneční skupiny"
      onClose={isSubmitting ? undefined : onClose}
      closeOnBackdrop={false}
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Zrušit
          </Button>
          <Button type="submit" form="registration-form" loading={isSubmitting}>
            {isSubmitting ? 'Registruji…' : 'Registrovat skupinu'}
          </Button>
        </>
      }
    >
      <form id="registration-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
        <p className="text-sm text-gray-600">
          Jeden účet patří jedné taneční skupině. Přihlašovat se budete e-mailem a heslem, které zde zvolíte.
        </p>
        <TextField
          label="Název taneční skupiny"
          name="organization"
          autoComplete="organization"
          value={data.groupName}
          onChange={set('groupName')}
          maxLength={100}
          required
        />
        <TextField
          label="Jméno a příjmení vedoucího"
          name="name"
          autoComplete="name"
          value={data.leaderName}
          onChange={set('leaderName')}
          maxLength={255}
          required
        />
        <TextField
          label="E-mail"
          type="email"
          name="email"
          autoComplete="email"
          inputMode="email"
          value={data.email}
          onChange={set('email')}
          maxLength={120}
          required
        />
        <TextField
          label="Telefon"
          type="tel"
          name="tel"
          autoComplete="tel"
          value={data.phone}
          onChange={set('phone')}
          maxLength={50}
          required
        />
        <PasswordField
          label="Heslo"
          name="new-password"
          autoComplete="new-password"
          value={data.password}
          onChange={set('password')}
          hint={`Alespoň ${MIN_PASSWORD} znaků.`}
          error={passwordTooShort ? `Heslo je zatím kratší než ${MIN_PASSWORD} znaků.` : null}
          required
        />
        <PasswordField
          label="Heslo znovu"
          name="confirm-password"
          autoComplete="new-password"
          value={data.confirmPassword}
          onChange={set('confirmPassword')}
          error={mismatch ? 'Hesla se neshodují.' : null}
          required
        />
        <label className="flex items-start gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={data.consent}
            onChange={set('consent')}
            className="mt-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            required
          />
          <span>
            Registrací skupiny souhlasíte se{' '}
            <a
              href="https://www.bestofthebest.cz/gdpr-a-cookies"
              target="_blank"
              rel="noopener noreferrer"
              className="font-medium text-indigo-700 underline hover:text-indigo-900"
            >
              zpracováním osobních údajů
            </a>
            .
          </span>
        </label>
        {error && <Alert tone="error">{error}</Alert>}
      </form>
    </Modal>
  );
}

function ForgotPasswordDialog({ onClose, initialEmail }) {
  const [email, setEmail] = useState(initialEmail || '');
  const [status, setStatus] = useState(null); // { tone, message }
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!value) {
      setStatus({ tone: 'error', message: 'Zadejte prosím e-mail, se kterým se přihlašujete.' });
      return;
    }
    setIsSubmitting(true);
    setStatus(null);
    try {
      await api.post('/auth/reset-password', { email: value });
      setStatus({
        tone: 'success',
        message:
          'Pokud je e-mail u nás registrovaný, poslali jsme na něj odkaz pro obnovení hesla (platí 30 minut). ' +
          `Pokud e-mail nedorazí do 10 minut, zkontrolujte složku Spam nebo nám napište na ${CONTACT_EMAIL}.`,
      });
    } catch (err) {
      setStatus({ tone: 'error', message: getErrorMessage(err) });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      title="Obnovení hesla"
      onClose={onClose}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {status?.tone === 'success' ? 'Zavřít' : 'Zrušit'}
          </Button>
          {status?.tone !== 'success' && (
            <Button type="submit" form="forgot-password-form" loading={isSubmitting}>
              Poslat odkaz
            </Button>
          )}
        </>
      }
    >
      <form id="forgot-password-form" onSubmit={handleSubmit} className="space-y-4" noValidate>
        <p className="text-sm text-gray-600">Pošleme vám e-mail s odkazem, přes který si nastavíte nové heslo.</p>
        <TextField
          label="E-mail"
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
        />
        {status &&
          (status.tone === 'success' ? (
            <p role="status" className="flex items-start gap-2 rounded-md bg-green-50 p-3 text-sm text-green-800">
              <CheckCircle className="mt-0.5 h-4 w-4 flex-shrink-0" aria-hidden="true" />
              {status.message}
            </p>
          ) : (
            <Alert tone="error">{status.message}</Alert>
          ))}
      </form>
    </Modal>
  );
}

const Login = () => {
  const { login, sessionExpired } = useAuth();
  const meta = useMeta();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dialog, setDialog] = useState(() => (location.state && location.state.forgotPassword ? 'forgot' : null));

  const expired = sessionExpired || Boolean(location.state && location.state.expired);
  const closeDialog = useCallback(() => setDialog(null), []);

  useEffect(() => {
    if (location.state && location.state.forgotPassword) setDialog('forgot');
  }, [location.state]);

  const handleLogin = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setError('');
    if (!email.trim() || !password) {
      setError('Vyplňte prosím e-mail i heslo.');
      return;
    }
    setIsSubmitting(true);
    const result = await login(email.trim(), password);
    if (!result.success) {
      setError(result.status === 401 ? 'Nesprávný e-mail nebo heslo.' : result.message || MESSAGES.unknown);
      setIsSubmitting(false);
    }
    // On success AuthContext sets the user and the /login route redirects.
  };

  return (
    <div className="flex min-h-screen flex-col justify-center bg-gray-100 px-4 py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <h1 className="text-center text-3xl font-bold text-indigo-700">Best of the Best</h1>
        <p className="mt-2 text-center text-sm text-gray-600">Registrace tanečních skupin na soutěže</p>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="rounded-lg bg-white px-4 py-8 shadow sm:px-10">
          {expired && (
            <Alert tone="info" className="mb-6">
              Vaše přihlášení vypršelo. Přihlaste se prosím znovu.
            </Alert>
          )}

          <form className="space-y-5" onSubmit={handleLogin} noValidate>
            <TextField
              id="email"
              label="E-mail"
              type="email"
              name="email"
              autoComplete="username"
              inputMode="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            <PasswordField
              id="password"
              label="Heslo"
              name="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            {meta.passwordResetEnabled && (
              <div className="text-right text-sm">
                <button
                  type="button"
                  onClick={() => setDialog('forgot')}
                  className="rounded font-medium text-indigo-700 hover:text-indigo-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
                >
                  Zapomenuté heslo?
                </button>
              </div>
            )}

            {error && <Alert tone="error">{error}</Alert>}

            <Button type="submit" className="w-full" loading={isSubmitting}>
              {isSubmitting ? 'Přihlašuji…' : 'Přihlásit se'}
            </Button>
          </form>

          {meta.registrationEnabled ? (
            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center" aria-hidden="true">
                  <div className="w-full border-t border-gray-300" />
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="bg-white px-2 text-gray-600">Nová skupina?</span>
                </div>
              </div>
              <Button variant="secondary" className="mt-6 w-full" onClick={() => setDialog('register')}>
                Registrovat taneční skupinu
              </Button>
            </div>
          ) : (
            <Alert tone="info" className="mt-6">
              Na testovací verzi se přihlašuje jen organizátor. Skupiny si zobrazíte v administraci přes „Zobrazit
              jako skupina“.
            </Alert>
          )}
        </div>
        <p className="mt-4 text-center text-sm text-gray-600">
          Potřebujete pomoc? Napište na{' '}
          <a href={`mailto:${CONTACT_EMAIL}`} className="font-medium text-indigo-700 hover:text-indigo-900">
            {CONTACT_EMAIL}
          </a>
        </p>
      </div>

      {dialog === 'register' && <RegistrationDialog onClose={closeDialog} />}
      {dialog === 'forgot' && <ForgotPasswordDialog onClose={closeDialog} initialEmail={email} />}
    </div>
  );
};

export default Login;
